import type { ParsedUrlQuery } from 'querystring';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

/**
 * A reservation or subscription renewal the venue holds for the buyer is paid
 * through a flow of its own, under `/reservation/[encryptedOrderId]` or
 * `/renewal/[encryptedOrderId]`. Being on one of those routes is what puts the
 * app in the flow: app state then reads and writes that order instead of the
 * buyer's cart, and leaving the routes hands the cart back untouched.
 */
export type DedicatedOrderKind = 'reservation' | 'renewal';
export type DedicatedOrderRoute = {
  kind: DedicatedOrderKind;
  /** Undefined on the first render of a statically optimised page, before the
   *  router has its query. The route is already known to be a flow then, so
   *  nothing may fall back to the cart in the meantime. */
  encryptedOrderId: string | undefined;
};
const routePattern = /^\/(reservation|renewal)\/\[encryptedOrderId\](?:\/|$)/;
export function parseDedicatedOrderRoute(pathname: string, query: ParsedUrlQuery): DedicatedOrderRoute | null {
  const match = routePattern.exec(pathname);
  if (!match) return null;
  const id = query.encryptedOrderId;
  return {
    kind: match[1] as DedicatedOrderKind,
    encryptedOrderId: typeof id === 'string' && id ? id : undefined
  };
}
export function useDedicatedOrderRoute(): DedicatedOrderRoute | null {
  const {
    pathname,
    query
  } = useRouter();
  const id = query.encryptedOrderId;
  return useMemo(() => parseDedicatedOrderRoute(pathname, {
    encryptedOrderId: id
  }), [pathname, id]);
}
export const getDedicatedOrderKind = (order: {
  isRenewal?: boolean | null;
}): DedicatedOrderKind => order.isRenewal ? 'renewal' : 'reservation';
export const getDedicatedOrderHref = (kind: DedicatedOrderKind, encryptedOrderId: string) => `/${kind}/${encodeURIComponent(encryptedOrderId)}`;

/** The My pages tab the flow was opened from, and returns to once the order
 *  is gone (paid, cancelled or let go of). */
export const getDedicatedOrderListHref = (kind: DedicatedOrderKind) => kind === 'renewal' ? '/account/renewals' : '/account/reservations';
export type OrderRoutes = {
  cart: string;
  checkout: string;
  checkoutDetails: string;
  checkoutSignIn: string;
  checkoutSignUpDetails: string;
  /** The event seat picker editing the order's own seats. */
  eventSeats: (eventGroupId: number, eventId: number) => string;
  /** The subscription seat picker editing one event of a subscription in the
   *  order. */
  subscriptionSeats: (subscriptionTypeId: number, editEventId: number) => string;
};

/**
 * Where the cart, checkout and seat-editing steps live for the order app state
 * is holding. Outside a flow they are the ordinary routes; inside one they sit
 * under the flow's prefix so moving between them never leaves it. The sign-in
 * steps have no flow counterpart — the flow is only reachable signed in.
 */
export function getOrderRoutes(route: DedicatedOrderRoute | null): OrderRoutes {
  if (!route) {
    return {
      cart: '/cart',
      checkout: '/checkout',
      checkoutDetails: '/checkout/details',
      checkoutSignIn: '/checkout/sign-in',
      checkoutSignUpDetails: '/checkout/sign-up-details',
      eventSeats: (eventGroupId, eventId) => `/${eventGroupId}/event/${eventId}?mode=order`,
      subscriptionSeats: (subscriptionTypeId, editEventId) => `/subscription/${subscriptionTypeId}/choose-seats?editEventId=${editEventId}`
    };
  }
  const base = getDedicatedOrderHref(route.kind, route.encryptedOrderId ?? '');
  return {
    cart: base,
    checkout: `${base}/checkout`,
    checkoutDetails: `${base}/checkout/details`,
    checkoutSignIn: `${base}/checkout`,
    checkoutSignUpDetails: `${base}/checkout/details`,
    eventSeats: (eventGroupId, eventId) => `${base}/${eventGroupId}/event/${eventId}?mode=order`,
    subscriptionSeats: (subscriptionTypeId, editEventId) => `${base}/subscription/${subscriptionTypeId}/choose-seats?editEventId=${editEventId}`
  };
}
export function useOrderRoutes(): OrderRoutes {
  const route = useDedicatedOrderRoute();
  return useMemo(() => getOrderRoutes(route), [route]);
}